import {
  Avatar,
  Button,
  makeStyles,
  Persona,
  TableCell,
  TableCellActions,
  TableCellLayout,
  TableColumnId,
  TableRow,
  tokens,
} from '@fluentui/react-components';
import React, { CSSProperties, useCallback } from 'react';
import { Segment } from '@axys-notes/common';
import { DropdownAutocomplete, DropdownValue } from '../dropdown-autocomplete';
import { EditRegular } from '@fluentui/react-icons';
import { EditableValue } from '../editable-value';

const useStyles = makeStyles({
  cardStyle: {
    backgroundColor: tokens.colorSubtleBackgroundHover,
    gap: 0,
  },
  actionsBar: {
    display: 'flex',
  },
  h2Style: { fontWeight: 'bold', paddingLeft: '15px' },
  headerCellStyle: { fontWeight: 'bold' },
  dropdownStyle: {},
});

export const MeetingTranscriptSegmentRow = (props: {
  isEditable: boolean;
  columnSizing: {
    getTableCellProps: (columnId: TableColumnId) => { style?: CSSProperties };
  };
  speakers: { [speakerId: string]: string };
  segment: Segment;
  segmentIndex: number;
  sameSpeaker?: boolean;
  isEditingSpeaker: boolean;
  onStartSpeakerEdit: (index: number) => void;
  onCancelSpeakerEdit: (index: number) => void;
  onSaveSpeakerEdit: (
    index: number,
    speakerId: string | undefined,
    speakerName: string
  ) => void;
  isEditingText: boolean;
  onStartTextEdit: (index: number) => void;
  onCancelTextEdit: (index: number) => void;
  onSaveTextEdit: (index: number, text: string) => void;
}) => {
  const {
    isEditable,
    segment,
    segmentIndex,
    speakers,
    columnSizing,
    sameSpeaker,
    isEditingSpeaker,
    onStartSpeakerEdit,
    onCancelSpeakerEdit,
    onSaveSpeakerEdit,
    isEditingText,
    onStartTextEdit,
    onCancelTextEdit,
    onSaveTextEdit,
  } = props;

  const editSpeaker = useCallback(() => {
    onStartSpeakerEdit(segmentIndex);
  }, [onStartSpeakerEdit, segmentIndex]);

  const speakerEditSave = useCallback(
    (value: DropdownValue) => {
      onSaveSpeakerEdit(segmentIndex, value.value, value.text);
    },
    [onSaveSpeakerEdit, segmentIndex]
  );

  const speakerEditCancel = useCallback(() => {
    onCancelSpeakerEdit(segmentIndex);
  }, [onCancelSpeakerEdit, segmentIndex]);

  const editText = useCallback(() => {
    onStartTextEdit(segmentIndex);
  }, [onStartTextEdit, segmentIndex]);

  const textEditSave = useCallback(
    (value: string) => {
      onSaveTextEdit(segmentIndex, value);
    },
    [onSaveTextEdit, segmentIndex]
  );

  const textEditCancel = useCallback(() => {
    onCancelTextEdit(segmentIndex);
  }, [onCancelTextEdit, segmentIndex]);

  const styles = useStyles();

  return (
    <TableRow>
      <TableCell
        style={{
          ...(columnSizing.getTableCellProps('speaker').style || {}),
        }}
      >
        <TableCellLayout
          truncate={!isEditingSpeaker}
          media={
            !isEditingSpeaker && !sameSpeaker ? (
              <Avatar
                size={20}
                aria-label={segment.speaker ? speakers[segment.speaker] : 'Unknown Speaker'}
                name={segment.speaker ? (speakers[segment.speaker] || '...') : 'Unknown Speaker'}
                color="colorful"
              />
            ) : undefined
          }
        >
          {!isEditingSpeaker ? (
            sameSpeaker ? (
              ''
            ) : (
              segment.speaker ? (speakers[segment.speaker] || '...') : 'Unknown Speaker'
            )
          ) : (
            <DropdownAutocomplete
              className={styles.dropdownStyle}
              options={speakers}
              originalValue={segment.speaker || undefined}
              renderOption={(option) => (
                <Persona
                  size="extra-small"
                  avatar={{ color: 'colorful', 'aria-hidden': true }}
                  name={option.text}
                />
              )}
              onSave={speakerEditSave}
              onCancel={speakerEditCancel}
            />
          )}
        </TableCellLayout>
        {isEditable && !isEditingSpeaker && (
          <TableCellActions>
            <Button
              icon={<EditRegular />}
              onClick={editSpeaker}
              appearance="subtle"
              aria-label="Éditer"
            />
          </TableCellActions>
        )}
      </TableCell>
      <TableCell {...columnSizing.getTableCellProps('segment')}>
        <TableCellLayout content={{ style: { flex: 1 } }}>
          <EditableValue
            onStartEditing={() => {
              // nothing
            }}
            onSave={textEditSave}
            onCancel={textEditCancel}
            value={segment.text}
            editing={isEditingText}
            editable={false}
            multiline={true}
          />
        </TableCellLayout>
        {isEditable && !isEditingText && (
          <TableCellActions>
            <Button
              icon={<EditRegular />}
              onClick={editText}
              appearance="subtle"
              aria-label="Éditer"
            />
          </TableCellActions>
        )}
      </TableCell>
      <TableCell {...columnSizing.getTableCellProps('timing')}>
        <TableCellLayout truncate>
          {new Date(segment.start * 1000).toISOString().substring(11, 19)}
        </TableCellLayout>
      </TableCell>
    </TableRow>
  );
};
